import {EventEmitter, inject, Injectable} from '@angular/core';
import {
  AuthControllerService,
  Contingent,
  FeatureConfig,
  Organisation,
  OrganisationControllerService,
  Project,
  ProjectControllerService,
  RoleResponse,
  StateControllerService,
  User
} from "../../api";
import {BehaviorSubject, forkJoin, Observable, of, Subject, Subscriber, Subscription, throwError, timer,} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {NavigationEnd, Router} from "@angular/router";
import {LoadingService} from "../loading/loading.service";
import {catchError, filter, map, retry, take} from "rxjs/operators";
import {SseService} from '../sse/sse.service';
import {ContingentUsageService} from '../contingent-usage.service';
import {SseEvent} from '../sse/sse-event.model';

/**
 * Wartezeiten der Wiederholungen von `GET /state`.
 *
 * Der State trägt die Projekt- und Organisationsrechte. Scheitert der erste Abruf (Replika im
 * Kaltstart, 502/504 vom Ingress beim Hochskalieren), blieb die Rechte-Map leer – alles mit
 * Projektrecht war ausgegraut, bis die SSE-Wiederverbindung Minuten später den State neu holte
 * (beta, 2026-10-07: Projekt-Admin konnte weder Rollen ändern noch einen myCheck starten).
 */
const STATE_RETRY_DELAYS_MS = [1000, 3000, 8000];

/** Nur vorübergehende Fehler wiederholen; 4xx (401 nach Tokenwechsel, 403) ändert ein zweiter Versuch nicht. */
function retryDelayFor(error: unknown, attempt: number): Observable<number> {
  const status = error instanceof HttpErrorResponse ? error.status : 0;
  const transient = status === 0 || status === 429 || status >= 500;
  return transient ? timer(STATE_RETRY_DELAYS_MS[attempt - 1]) : throwError(() => error);
}

@Injectable({
  providedIn: 'root'
})
export class StateConfigService {
  private stateControllerService = inject(StateControllerService);
  private router = inject(Router);
  private loadingService = inject(LoadingService);
  private sseService = inject(SseService);
  private projectControllerService = inject(ProjectControllerService);
  private organisationControllerService = inject(OrganisationControllerService);
  public user!: User;


  public runningChecks: string[] = [];
  public runningIfcChecks: string[] = [];
  public runningQualityChecks: string[] = [];
  public runningBimBaseChecks: string[] = [];
  public runningClassifications: string[] = [];

  /**
   * Observable-Variante von runningClassifications.
   * Vorteil: Komponenten können per async pipe binden; Updates passieren unabhängig vom aktuellen View.
   */
  private readonly runningClassificationsSetSubject = new BehaviorSubject<Set<string>>(new Set<string>());
  public readonly runningClassificationsSet$ = this.runningClassificationsSetSubject.asObservable();
  private readonly classificationCompletedSubject = new Subject<string>();
  public readonly classificationCompleted$ = this.classificationCompletedSubject.asObservable();

  /**
   * Multicast-sicheres Signal, dass ein Check abgeschlossen wurde (SSE CHECK_COMPLETED).
   * Anders als `updated`/`runningChecksChange` (Single-Subscriber-Observables) erhalten
   * hier alle Subscriber das Event – z.B. die Berichtsübersicht für ein Sofort-Refresh.
   */
  private readonly checkCompletedSubject = new Subject<string>();
  public readonly checkCompleted$ = this.checkCompletedSubject.asObservable();

  public isRunningClassification(id: string | null | undefined): boolean {
    if (!id) return false;
    return this.runningClassificationsSetSubject.value.has(id);
  }

  private subscription!: Subscription;
  /**
   * Roher Weiterversand der Ereignisse, die dieser Service nicht selbst verarbeitet.
   *
   * Der SSE-Strom laeuft zentral hier; wer ein Ereignis braucht (z.B. der DeleteService fuer den
   * Loesch-Fortschritt), abonniert diesen Strom statt eine zweite Verbindung aufzubauen.
   */
  private readonly sseEventsSubject = new Subject<SseEvent<any>>();
  public readonly sseEvents$ = this.sseEventsSubject.asObservable();

  private readonly sseReconnectsSubject = new Subject<void>();

  /**
   * Feuert, NACHDEM der State-Strom wieder steht und der Serverstand frisch geholt ist.
   *
   * Die Reihenfolge ist der Sinn der Sache: waehrend der Unterbrechung gefeuerte Ereignisse sind
   * verloren, wer daraus Zustand fortschreibt (Loesch-Batch, Klassifizierung) muss also abgleichen.
   * Das geht nur gegen frische Serverdaten - deshalb erst {@link updateData}, dann das Signal.
   */
  public readonly sseReconnects$ = this.sseReconnectsSubject.asObservable();


  private featureConfigs: FeatureConfig[] = [];
  public selectedFeatureConfig!: FeatureConfig;

  public projects: Map<number, RoleResponse> = new Map<number, RoleResponse>();
  public organisations: Map<number, RoleResponse> = new Map<number, RoleResponse>();
  /**
   * Plattform-globale Privilegien des Nutzers (nicht projekt-/org-gebunden), z.B.
   * GLOBAL_CHECKRULE_ADMIN. Per manuellem DB-Eintrag vergeben.
   */
  public globalPrivileges: RoleResponse.PrivilegesEnum[] = [];

  public selectedProject!: Project;
  public selectedOrganisation!: Organisation;

  private userContingent!: Contingent;
  private authControllerService = inject(AuthControllerService);

  public stage!: string

  /**
   * ID des zentral bereitgestellten Prüflaufs, den die HOAI-Kachel des All-In Checks startet
   * ({@code bic.all-in.hoai-definition-id} im Backend). Leer/undefined = nicht konfiguriert.
   */
  public hoaiDefinitionId?: string;

  /** Wie {@link hoaiDefinitionId}, für die LOIplus-Kachel ({@code bic.all-in.loiplus-definition-id}). */
  public loiplusDefinitionId?: string;

  private updateSubscriber!: Subscriber<void>
  public updated: Observable<void> = new Observable<void>(observer => {
    this.updateSubscriber = observer;
  });

  private runningChecksSubscriber!: Subscriber<string[]>
  public runningChecksChange: Observable<string[]> = new Observable<string[]>(observer => {
    this.runningChecksSubscriber = observer;
  });

  private runningIfcChecksSubscriber!: Subscriber<string[]>
  public runningIfcChecksChange: Observable<string[]> = new Observable<string[]>(observer => {
    this.runningIfcChecksSubscriber = observer;
  });

  private runningQualityChecksSubscriber!: Subscriber<string[]>
  public runningQualityChecksChange: Observable<string[]> = new Observable<string[]>(observer => {
    this.runningQualityChecksSubscriber = observer;
  });

  private runningBimBaseChecksSubscriber!: Subscriber<string[]>
  public runningBimBaseChecksChange: Observable<string[]> = new Observable<string[]>(observer => {
    this.runningBimBaseChecksSubscriber = observer;
  });

  private runningClassificationsSubscriber!: Subscriber<string[]>
  public runningClassificationsChange: Observable<string[]> = new Observable<string[]>(observer => {
    this.runningClassificationsSubscriber = observer;
  });

  public organisationChange: EventEmitter<void> = new EventEmitter<void>();
  public projectChange: EventEmitter<Project> = new EventEmitter<Project>();

  private readonly contingentUsageService = inject(ContingentUsageService);

  get contingent(): Contingent {
    return this.selectedOrganisation ? this.selectedOrganisation.contingent! : this.userContingent;
  }

  /**
   * Liefert das Contingent des Projekt-Eigentümers (nicht des eingeloggten Nutzers/der ausgewählten Organisation).
   * Bezahlt wird ein Check immer vom Projekt-Eigentümer, daher muss z.B. der Credit-Popup vor Checkstart
   * dieses und nicht `contingent` verwenden.
   */
  public getProjectContingent(projectId?: number): Contingent {
    return this.projects.get(projectId ?? this.getProjectId())?.contingent ?? {};
  }

  get filesUploaded(): boolean {
    return (this.selectedProject?.numberOfFiles ?? 0) > 0;
  }

  public start(): void {
    this.isPersonal = localStorage.getItem("isPersonal") == "true";
    let selectedProjectTemp = Number(localStorage.getItem("projectId"));
    this.selectOrganisation(Number(localStorage.getItem("organisation")), false, this.isPersonal);
    this.selectProject(selectedProjectTemp, false);
    let firstTime = true;

    this.authControllerService.profile().subscribe(profile => {
      this.user = profile;
    })


    this.updateData().subscribe(() => {
      this.subscribeToStateSse();

      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          const urlParts = event.url.split("/");
          if (urlParts.length > 1 && urlParts[1] === "projects") {
            if (firstTime) {
              firstTime = false;
              this.selectOrganisation(Number(localStorage.getItem("organisation")), false, this.isPersonal);
            }
            this.selectProject(Number(urlParts[2]), false);
          }
        }
      });

      this.runningClassificationsChange.subscribe(classifications => {
      });
    })


  }

  public stop(): void {
    if (this.stateSseRetryTimeout) {
      clearTimeout(this.stateSseRetryTimeout);
      this.stateSseRetryTimeout = null;
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /** Backoff der State-SSE-Wiederverbindung (siehe subscribeToStateSse). */
  private stateSseRetryDelayMs = 3000;
  private stateSseRetryTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Abonniert den State-Stream und haelt ihn am Leben.
   *
   * `SseService.listen` verbindet selbst neu, solange der Stream nur abbricht oder mit 503
   * (serverseitiger SSE-Timeout) antwortet. Ein 401 refresht es selbst einmal und verbindet neu;
   * scheitert der Refresh, ist abgemeldet und {@link stop} hat diesen Strom schon beendet. Bei jedem
   * anderen Nicht-2xx - etwa 502/504 vom Ingress waehrend eines Deployments - beendet es das
   * Observable mit einem Fehler.
   * Der wurde hier nur geloggt: der Stream war damit fuer den Rest der Sitzung tot, und
   * CLASSIFICATION_COMPLETED/CHECK_COMPLETED kamen bis zum manuellen Reload nicht mehr an - genau
   * das Bild von "Klassifizierungen aktualisieren sich nur nach einem Refresh".
   */
  private subscribeToStateSse(): void {
    this.ensureReconnectRecovery();
    if (this.stateSseRetryTimeout) {
      clearTimeout(this.stateSseRetryTimeout);
      this.stateSseRetryTimeout = null;
    }

    this.subscription?.unsubscribe();
    this.subscription = this.sseService.listen<any>('/state/subscribe').subscribe({
      next: (event: SseEvent<any>) => {
        this.stateSseRetryDelayMs = 3000;
        switch (event.type) {
          case 'CHECK_COMPLETED':
            this.removeRunningCheck(event.data as string);
            break;
          case 'CLASSIFICATION_COMPLETED':
            this.removeRunningClassification(event.data as string);
            // Zusaetzlich weiterreichen: der ClassificationService zaehlt damit den Fortschritt
            // eines Upload-Stapels mit. Ohne das blieb die Anzeige auf 0/381 stehen - das Ereignis
            // wurde hier verarbeitet, aber nie weitergegeben.
            this.sseEventsSubject.next(event);
            break;
          case 'FEATURE_CONFIG_CHANGED':
            this.applyFeatureConfigUpdate(event.data as FeatureConfig);
            break;
          case 'FILE_DELETION_PROGRESS':
          case 'FILE_DELETION_COMPLETED':
          case 'SOURCE_LINK_SYNCED':
          case 'SOURCE_LINK_SYNC_PROGRESS':
            // Nur weiterreichen: DeleteService bzw. Dateiansicht (verknuepfte Ordner) haengen daran, sollen aber keinen eigenen
            // SSE-Strom aufbauen - eine zweite Verbindung fuer dieselben Ereignisse waere Verschwendung.
            this.sseEventsSubject.next(event);
            break;
        }
      },
      error: err => {
        console.warn('State SSE error:', err);
        this.scheduleStateSseReconnect();
      },
      complete: () => this.scheduleStateSseReconnect()
    });
  }

  /**
   * Haengt sich EINMAL an die stillen Wiederverbindungen des SSE-Stroms.
   *
   * `SseService.listen` baut den Strom nach dem serverseitigen Timeout selbst neu auf, ohne dass
   * jemand davon erfaehrt - der Fehler-/Complete-Pfad unten greift dabei gar nicht. Genau in diesem
   * Fenster gehen Ereignisse verloren (im Backend-Log alle paar Minuten als
   * `AsyncRequestTimeoutException` zu sehen), und ohne Abgleich blieben Anzeigen stehen, deren
   * Abschlussmeldung hineingefallen ist.
   */
  private ensureReconnectRecovery(): void {
    if (this.reconnectRecoverySubscription) return;

    this.reconnectRecoverySubscription = this.sseService.reconnected$
      .pipe(filter(path => path === '/state/subscribe'))
      .subscribe(() => {
        // Erst der Serverstand, dann das Signal: die Abonnenten gleichen gegen diese Daten ab.
        this.updateData().subscribe({
          next: () => this.sseReconnectsSubject.next(),
          error: () => this.sseReconnectsSubject.next(),
        });
      });
  }

  private reconnectRecoverySubscription: Subscription | null = null;

  private scheduleStateSseReconnect(): void {
    if (this.stateSseRetryTimeout) return;
    // Abgemeldet (etwa weil der Token-Refresh scheiterte, waehrend start() noch lief): nicht weiter
    // verbinden. Nach dem naechsten Login baut start() den Strom neu auf.
    if (!localStorage.getItem('token')) return;

    const delay = this.stateSseRetryDelayMs;
    this.stateSseRetryDelayMs = Math.min(delay * 2, 30000);

    this.stateSseRetryTimeout = setTimeout(() => {
      this.stateSseRetryTimeout = null;
      // Erst den Serverstand holen: waehrend der Unterbrechung gefeuerte Events sind verloren,
      // `runningClassifications` und die laufenden Checks sonst dauerhaft veraltet.
      this.updateData().subscribe({
        next: () => this.subscribeToStateSse(),
        error: () => this.subscribeToStateSse()
      });
    }, delay);
  }

  private removeRunningCheck(checkId: string): void {
    const prevIfc = this.runningIfcChecks;
    const prevQuality = this.runningQualityChecks;
    const prevBimBase = this.runningBimBaseChecks;

    this.runningIfcChecks = this.runningIfcChecks.filter(id => id !== checkId);
    this.runningQualityChecks = this.runningQualityChecks.filter(id => id !== checkId);
    this.runningBimBaseChecks = this.runningBimBaseChecks.filter(id => id !== checkId);
    this.runningChecks = this.runningChecks.filter(id => id !== checkId);

    if (JSON.stringify(prevIfc) !== JSON.stringify(this.runningIfcChecks)) {
      this.runningIfcChecksSubscriber?.next(this.runningIfcChecks);
    }
    if (JSON.stringify(prevQuality) !== JSON.stringify(this.runningQualityChecks)) {
      this.runningQualityChecksSubscriber?.next(this.runningQualityChecks);
    }
    if (JSON.stringify(prevBimBase) !== JSON.stringify(this.runningBimBaseChecks)) {
      this.runningBimBaseChecksSubscriber?.next(this.runningBimBaseChecks);
    }
    this.runningChecksSubscriber?.next(this.runningChecks);
    this.checkCompletedSubject.next(checkId);
    this.updateSubscriber?.next();
  }

  private removeRunningClassification(fileId: string): void {
    const prev = this.runningClassifications;
    this.runningClassifications = this.runningClassifications.filter(id => id !== fileId);
    const newSet = new Set(this.runningClassifications);
    this.runningClassificationsSetSubject.next(newSet);
    this.classificationCompletedSubject.next(fileId);
    if (JSON.stringify(prev) !== JSON.stringify(this.runningClassifications)) {
      this.runningClassificationsSubscriber?.next(this.runningClassifications);
    }
    this.updateSubscriber?.next();
  }

  private applyFeatureConfigUpdate(featureConfig: FeatureConfig): void {
    const index = this.featureConfigs.findIndex(
      fc => fc['orgaId'] === featureConfig['orgaId']
    );
    if (index >= 0) {
      this.featureConfigs[index] = featureConfig;
    } else {
      this.featureConfigs.push(featureConfig);
    }
    this.selectedFeatureConfig = this.getCurrentFeatureConfig();
    this.updateSubscriber?.next();
  }

  /**
   * Übernimmt eine gespeicherte Organisation in den globalen State, damit z. B. die Sidebar
   * den neuen Namen ohne Reload anzeigt.
   */
  public organisationUpdated(organisation: Organisation): void {
    if (organisation.id == null) {
      return;
    }
    const role = this.organisations.get(organisation.id);
    if (role) {
      role.name = organisation.name;
    }
    if (this.selectedOrganisation?.id === organisation.id) {
      this.selectedOrganisation = {...this.selectedOrganisation, ...organisation};
    }
  }

  public update(): Observable<boolean> {
    return this.updateData();
  }

  /**
   * Holt den Serverzustand neu (laufende Checks, laufende Klassifizierungen, Feature-Config).
   *
   * Oeffentlich, weil Verbraucher, die Zustand aus SSE-Ereignissen fortschreiben, gegen frische
   * Serverdaten abgleichen muessen - ein verpasstes Ereignis waere sonst dauerhaft. Genau derselbe
   * Aufruf, den auch die SSE-Wiederverbindung macht.
   */
  public refreshServerState(): Observable<boolean> {
    return this.updateData();
  }

  private updateData(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      let previousRunningChecks = this.runningChecks;
      let previousRunningIfcChecks = this.runningIfcChecks;
      let previousRunningQualityChecks = this.runningQualityChecks;
      let previousRunningBimBaseChecks = this.runningBimBaseChecks;
      let previousRunningClassifications = this.runningClassifications;

      this.stateControllerService.getState(this.getProjectId().toString()).pipe(
        retry({count: STATE_RETRY_DELAYS_MS.length, delay: (error, attempt) => retryDelayFor(error, attempt)}),
      ).subscribe(state => {
        this.runningIfcChecks = state.projectRunningIfcChecksData ?? [];
        this.runningQualityChecks = state.projectRunningQualityChecksData ?? [];
        this.runningBimBaseChecks = state.projectRunningBimBaseChecksData ?? [];
        this.runningClassifications = state.runningClassifications ?? [];

        // keep Set/Observable in sync
        this.runningClassificationsSetSubject.next(new Set(this.runningClassifications));

        this.featureConfigs = state.featureConfigs!;
        this.selectedFeatureConfig = this.getCurrentFeatureConfig();

        this.userContingent = state.contingent!;

        // Speicherstand des Mandanten mitziehen - gedrosselt, s. refreshStorageUsage(). Bewusst
        // hier und nicht in der Projektauswahl: der Verbrauch aendert sich mit jedem Upload und
        // jeder Loeschung, nicht nur beim Wechsel des Mandanten.
        this.refreshStorageUsage();

        this.stage = state.stage!;

        // Über einen Cast gelesen: der generierte Client kennt das Feld erst nach dem
        // nächsten `./api-client-generation.sh` gegen ein Backend mit dieser Property.
        const allInConfig = state as unknown as {
          hoaiDefinitionId?: string;
          loiplusDefinitionId?: string;
        };
        this.hoaiDefinitionId = allInConfig.hoaiDefinitionId || undefined;
        this.loiplusDefinitionId = allInConfig.loiplusDefinitionId || undefined;

        this.globalPrivileges = state.globalPrivileges ?? [];

        for (let projectsKey in state.projects) {
          this.projects.set(Number(projectsKey), state.projects[projectsKey]);
        }

        for (let organisationsKey in state.organisations) {
          this.organisations.set(Number(organisationsKey), state.organisations[organisationsKey]);
          if (this.selectedOrganisation?.id === Number(organisationsKey)) {
            this.selectedOrganisation.name = state.organisations[organisationsKey].name;
          }
        }

        if (JSON.stringify(previousRunningIfcChecks) !== JSON.stringify(this.runningIfcChecks)) {
          this.runningIfcChecksSubscriber?.next(this.runningIfcChecks ?? []);
        }

        if (JSON.stringify(previousRunningQualityChecks) !== JSON.stringify(this.runningQualityChecks)) {
          this.runningQualityChecksSubscriber?.next(this.runningQualityChecks ?? []);
        }

        if (JSON.stringify(previousRunningBimBaseChecks) !== JSON.stringify(this.runningBimBaseChecks)) {
          this.runningBimBaseChecksSubscriber?.next(this.runningBimBaseChecks ?? []);
        }


        if (JSON.stringify(previousRunningClassifications) !== JSON.stringify(this.runningClassifications)) {
          this.runningClassificationsSubscriber?.next(this.runningClassifications ?? []);
        }

        if (JSON.stringify(previousRunningChecks) !== JSON.stringify(this.runningChecks)) {
          this.runningChecksSubscriber?.next(this.runningChecks ?? []);
        }

        this.updateSubscriber?.next();

        observer.next(true);
        observer.complete();
      }, error => {
        console.error('State update failed:', error);
        observer.next(false);
        observer.complete();
      });
    });
  }

  public getOrganisationId(): number {
    // localStorage wird in selectOrganisation synchron gesetzt, selectedOrganisation erst nach dem
    // Laden - sonst liefert ein Org-Wechsel kurzzeitig noch die ID der vorherigen Organisation.
    const storedId = Number(localStorage.getItem("organisation"));
    if (storedId) {
      return storedId;
    }
    return this.selectedOrganisation?.id ?? 0;
  }

  public getOrganisationIdOrUndefined(): number | undefined {
    let organisationId = this.getOrganisationId();
    return organisationId === 0 ? undefined : organisationId;
  }

  public getProject(): Project | undefined {
    return this.selectedProject;
  }

  public getProjectId(): number {
    return this.selectedProject?.id ? this.selectedProject.id : Number(localStorage.getItem("projectId"));
  }

  public getProjectIdOrUndefined(): number | undefined {
    let projectId = this.getProjectId();
    return projectId === 0 ? undefined : projectId;
  }

  public get isPersonal(): boolean {
    return localStorage.getItem("isPersonal") === "true";
  }

  public set isPersonal(isPersonal: boolean) {
    localStorage.setItem("isPersonal", isPersonal ? "true" : "false");
  }

  public getOrganisation(): Organisation | undefined {
    return this.selectedOrganisation;
  }

  public selectOrganisation(organisationId: number | undefined, navigate: boolean = true, isPersonal: boolean = false, deselect: boolean = false): void {

    localStorage.removeItem("project");
    if (deselect) {
      localStorage.setItem("organisation", "");
      localStorage.setItem("isPersonal", "false")
      this.selectedOrganisation = undefined!;
      this.selectedProject = undefined!;
      if(navigate){
        this.router.navigateByUrl(`organisations`);
      }
    } else {
      localStorage.setItem("organisation", organisationId?.toString() ?? "");
      localStorage.setItem("isPersonal", isPersonal ? "true" : "false")
      this.isPersonal = isPersonal;
      this.organisationChange.emit();

      if (organisationId) {
        this.organisationControllerService.getOrganisation(organisationId).subscribe(organisation => {
          this.selectedOrganisation = organisation;
          // Recompute the feature config now that the selected organisation is available.
          this.selectedFeatureConfig = this.getCurrentFeatureConfig();
          this.updateSubscriber?.next();
        });
      } else {
        this.selectedOrganisation = undefined!;
        // Recompute the feature config for the personal context.
        this.selectedFeatureConfig = this.getCurrentFeatureConfig();
        this.updateSubscriber?.next();
      }

      if (this.selectedProject && navigate) {
        this.router.navigateByUrl(`organisations`);
      } else if (navigate) {
        this.router.navigateByUrl(`projects`);
      }


    }
  }

  public selectProject(projectId: number | undefined, navigate: boolean = true): void {
    localStorage.setItem("projectId", projectId?.toString() ?? "")

    if (projectId) {
      const localProject = localStorage.getItem("project");
      if (localProject) {
        const parsedProject: Project = JSON.parse(localProject);
        if (parsedProject.id === projectId) {
          this.selectedProject = parsedProject;
          if (navigate) {
            this.projectChange.emit(this.selectedProject);
            this.router.navigateByUrl(`projects/${parsedProject.id}`);
          }
        }
      } else {
        this.projectControllerService.getProject(projectId).subscribe(project => {
          this.selectedProject = project;
          localStorage.setItem("project", JSON.stringify(project));
          if (navigate) {
            this.projectChange.emit(this.selectedProject);
            this.router.navigateByUrl(`projects/${project.id}`);
          }

        });
      }


    } else {
      if (navigate) {
        this.router.navigateByUrl(`projects`).then(() => {
          this.selectedProject = undefined!;
          localStorage.removeItem("project");
          localStorage.removeItem("projectId");
          this.projectChange.emit(undefined!);
          this.router.navigateByUrl(`projects`);
        });
      }else{
        this.selectedProject = undefined!;
        localStorage.removeItem("project");
        localStorage.removeItem("projectId");
        this.projectChange.emit(undefined!);
      }
    }

  }

  public selectOrganisationForProject(project: Project, retryAttempted: boolean = false): void {
    // Validate that project has an ID
    if (!project.id) {
      // Project without ID cannot be associated with an organization
      return;
    }

    // If it's a personal project, set isPersonal to true
    if (project.organisationProject === false) {
      this.selectOrganisation(undefined, false, true);
      return;
    }

    // It's an organization project - find which organization it belongs to
    // Get all organization IDs from the organisations map
    const organisationIds = Array.from(this.organisations.keys());

    if (organisationIds.length === 0) {
      if (!retryAttempted) {
        // No organizations loaded yet - wait for updateData to complete
        // This happens on initial page load when ProjectResolver runs before updateData completes
        this.updated.pipe(take(1)).subscribe(() => {
          // Retry after data is loaded (only once)
          this.selectOrganisationForProject(project, true);
        });
      } else {
        // Already retried and still no organizations - treat as personal project
        this.selectOrganisation(undefined, false, true);
      }
      return;
    }

    if (organisationIds.length === 1) {
      // Only one organization - must be the owner
      this.selectOrganisation(organisationIds[0], false, false);
      return;
    }

    // Multiple organizations - try to find the correct one
    // First check if localStorage has a valid organization
    const storedOrgId = Number(localStorage.getItem("organisation"));
    if (storedOrgId && this.organisations.has(storedOrgId)) {
      // Verify this organization actually contains the project
      this.projectControllerService.getProjectsByOrganisationId(storedOrgId).pipe(
        take(1),
        catchError(() => {
          // If this fails, fall back to searching all organizations
          this.findOrganisationForProjectParallel(project.id!, organisationIds);
          // Return empty observable to prevent further execution in subscribe
          return of(null);
        })
      ).subscribe(projects => {
        // If catchError returned null, skip this branch
        if (projects === null) {
          return;
        }

        const found = projects.some(p => p.id === project.id);
        if (found) {
          this.selectOrganisation(storedOrgId, false, false);
        } else {
          // Stored organization doesn't contain this project, search for the right one
          this.findOrganisationForProjectParallel(project.id!, organisationIds);
        }
      });
    } else {
      // No valid stored organization, search for the right one
      this.findOrganisationForProjectParallel(project.id!, organisationIds);
    }
  }

  private findOrganisationForProjectParallel(projectId: number, organisationIds: number[]): void {
    // Guard against empty array
    if (organisationIds.length === 0) {
      return;
    }

    // Fetch projects for all organizations in parallel
    const requests = organisationIds.map(orgId =>
      this.projectControllerService.getProjectsByOrganisationId(orgId).pipe(
        take(1),
        map(projects => ({ orgId, projects })),
        catchError(() => of({ orgId, projects: [] }))
      )
    );

    forkJoin(requests).pipe(
      take(1)
    ).subscribe(results => {
      // Find which organization contains the project
      const match = results.find(result =>
        result.projects.some(p => p.id === projectId)
      );

      if (match) {
        this.selectOrganisation(match.orgId, false, false);
      } else {
        // No match found, fall back to first organization
        this.selectOrganisation(organisationIds[0], false, false);
      }
    });
  }

  /**
   * Gesamtguthaben des Projekt-Kontingents: Abo-Credits plus Add-on-Credits.
   *
   * <p>Beide Töpfe zusammen sind das, was der Server abbucht (`Contingent.spendCredits`), und
   * genau das, was die `*Checks`-Werte einrechnen. Wer nur `credits` anzeigt, meldet einem Kunden
   * mit Add-on-Guthaben „nicht genügend Credits", während der Server die Prüfung erlaubt.
   */
  public getTotalCredits(): number {
    const contingent = this.projects.get(this.getProjectId())?.contingent;
    return (contingent?.credits ?? 0) + (contingent?.addOnCredits ?? 0);
  }

  /** Der nicht verfallende Anteil des Guthabens. */
  public getAddOnCredits(): number {
    return this.projects.get(this.getProjectId())?.contingent?.addOnCredits ?? 0;
  }

  public canCheckHOAI(): boolean {
    return (this.projects.get(this.getProjectId())?.contingent?.hoaiChecks ?? 0) > 0;
  }

  public canCheckLOI(): boolean {
    return (this.projects.get(this.getProjectId())?.contingent?.loiChecks ?? 0) > 0;
  }

  public canCheckQuality(): boolean {
    return (this.projects.get(this.getProjectId())?.contingent?.qualityChecks ?? 0) > 0;
  }

  public canCheckBimBase(): boolean {
    return (this.projects.get(this.getProjectId())?.contingent?.bimBaseChecks ?? 0) > 0;
  }

  /** MyCheck-Workflows (LOI/IDS/HYBRID/AI) kosten pauschal 100 Credits pro Regeltyp. */
  public canCheckMyCheck(): boolean {
    return (this.projects.get(this.getProjectId())?.contingent?.myChecks ?? 0) > 0;
  }

  public canDownloadBuildingApplication(): boolean {
    return (this.projects.get(this.getProjectId())?.contingent?.buildingApplications ?? 0) > 0;
  }

  public canAddProjectMember(): boolean {
    return this.projects.get(this.getProjectId())?.contingent?.trialEnd == null;
  }

  /**
   * Speicherstand des MANDANTEN, wie ihn der Server aufloest - oder null, solange nichts geladen ist.
   *
   * <p>Hier stand bis 2026-09-18 etwas anderes, und es war gleich doppelt falsch: der Verbrauch kam
   * aus {@code selectedProject.fileSize} (also EIN Projekt, waehrend die Grenze fuer den ganzen
   * Mandanten gilt - wer drei Projekte hat, sah ein Drittel), und die Grenze aus dem Kontingent des
   * ausgewaehlten Projekts mit einem erfundenen {@code ?? 100} GB dahinter. Diese 100 GB standen in
   * keiner Datenbank: sie waren die dritte Quelle neben Plan und Kontingent und der Grund, warum die
   * Abo-Info 200 GB zeigte, waehrend der Upload bei 100 warnte.</p>
   *
   * <p>Jetzt kommt beides aus derselben Quelle wie die Anzeige und wie die serverseitige Sperre:
   * {@code /contingent/usage}.</p>
   */
  private storageSnapshot: { usedBytes: number; limitBytes: number | null } | null = null;
  private storageSnapshotAt = 0;
  /** Wie lange ein Stand als frisch genug fuer die Vorwarnung gilt. */
  private static readonly STORAGE_SNAPSHOT_TTL_MS = 60_000;

  /**
   * Holt den Speicherstand des Mandanten nach - hoechstens einmal je Minute.
   *
   * <p>Gedrosselt, weil {@code updateData()} im Sekundentakt laufen kann (Fallback-Poll der
   * Dateiansicht) und die Summe ueber alle Projektdateien nichts ist, was man dabei mitzieht.
   * {@code force} umgeht die Drosselung - fuer den Moment nach einem Upload, wo sich der Verbrauch
   * tatsaechlich geaendert hat.</p>
   */
  public refreshStorageUsage(force = false): void {
    const now = Date.now();
    if (!force && now - this.storageSnapshotAt < StateConfigService.STORAGE_SNAPSHOT_TTL_MS) {
      return;
    }
    this.storageSnapshotAt = now;

    this.contingentUsageService.getUsage(this.getOrganisationIdOrUndefined()).subscribe({
      next: usage => {
        const storage = usage.limits.find(limit => limit.key === 'storage');
        this.storageSnapshot = storage
          ? {usedBytes: storage.used, limitBytes: storage.limit}
          : null;
      },
      // Der alte Stand bleibt stehen. Ihn zu verwerfen hiesse, die Vorwarnung abzuschalten, obwohl
      // wir die Grenze vorhin noch kannten.
      error: () => undefined,
    });
  }

  /** Belegter Speicher des Mandanten in Bytes; 0, solange nichts geladen ist. */
  public getStorageUsageBytes(): number {
    return this.storageSnapshot?.usedBytes ?? 0;
  }

  /** Grenze in Bytes, {@code null} = unbegrenzt ODER noch nicht bekannt. */
  public getStorageLimitBytes(): number | null {
    return this.storageSnapshot?.limitBytes ?? null;
  }

  /** Grenze in GB fuer die Anzeige, {@code null} wie oben. */
  public getStorageLimitGb(): number | null {
    const bytes = this.getStorageLimitBytes();
    return bytes === null ? null : bytes / (1024 * 1024 * 1024);
  }

  /** Auslastung in Prozent; 0, wenn es keine Grenze gibt oder sie unbekannt ist. */
  public getStorageUsagePercent(): number {
    const limit = this.getStorageLimitBytes();
    if (limit === null) return 0;
    if (limit === 0) return 100;
    return (this.getStorageUsageBytes() / limit) * 100;
  }

  /**
   * Vorwarnung vor dem Upload - bewusst nur eine Vorwarnung.
   *
   * <p>Die verbindliche Pruefung steht serverseitig ({@code ProjectFileController} gegen
   * {@code TenantLimitsService}); hier geht es darum, dem Nutzer 500 Dateien zu ersparen, die
   * hinterher abgelehnt werden.</p>
   *
   * <p>Ist die Grenze unbekannt, wird NICHT gesperrt. Frueher stand hier ein erfundener Wert von
   * 100 GB, und der hat in beide Richtungen gelogen: Kunden mit groesserem Plan wurden zu frueh
   * gebremst, und wer weniger hatte, lief trotzdem erst serverseitig auf. Lieber einmal zu viel
   * durchlassen und den Server entscheiden lassen als eine Zahl erfinden.</p>
   */
  public canUploadFiles(additionalBytes: number): boolean {
    const limit = this.getStorageLimitBytes();
    if (limit === null) return true;
    return this.getStorageUsageBytes() + additionalBytes <= limit;
  }

  private getCurrentFeatureConfig(): FeatureConfig {
    // Use getOrganisationId() (reads from localStorage synchronously, returns 0 for personal context)
    // instead of this.selectedOrganisation?.id, because selectedOrganisation is loaded asynchronously
    // and may not be set yet when this runs. The backend returns orgaId === 0 for the personal/user
    // based config, so we normalize orgaId with `?? 0` for a reliable match.
    const currentOrganisationId = this.getOrganisationId();
    let featureConfigOfCurrentOrganisation = this.featureConfigs.find(featureConfig => (featureConfig.orgaId ?? 0) === currentOrganisationId);
    // Fallback to the personal/user based config (orgaId 0 / null / undefined).
    return featureConfigOfCurrentOrganisation ?? this.featureConfigs.find(featureConfig => (featureConfig.orgaId ?? 0) === 0)!;
  }

  public getFeatureConfig(): FeatureConfig {
    return this.selectedFeatureConfig;
  }

  /**
   * Für den aktuellen Kontext freigeschaltete KI-Modelle, aufgelöst vom Backend
   * (`AiModelPolicyService`): das Standardmodell zuerst, danach die per FeatureConfig
   * freigeschalteten Zusatzmodelle. Genau ein Eintrag heißt „keine Auswahl".
   */
  public getAiModels(): string[] {
    return this.getFeatureConfig()?.aiModels ?? [];
  }

  /**
   * Vorgabemodell für ein neues Prüfregelset. Bewusst hier und nicht als Konstante in den
   * Editoren: welches Modell gilt, entscheidet das Backend pro Kunde.
   */
  public getDefaultAiModel(): string {
    return this.getAiModels()[0] ?? '';
  }


}
